// client/src/components/plataforma/siteInstitucional/RedesSociaisFormPage.js
// Nenhuma alteração necessária, o código anterior já estava correto para este arquivo.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSocialConfigs } from '../../../hooks/useSocialConfigs';
import { Save, Share2, Loader2 } from 'lucide-react';
import PageHeader from '../../layout/PageHeader';
import FabSaveButton from '../../common/FabSaveButton';
import useAlert from '../../../hooks/useAlert'; // Certifique-se de que este caminho está correto

const RedesSociaisFormPage = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditing = !!id;
    const { createSocialConfig, updateSocialConfig, getSocialConfig, platformsOptions, loading: hookLoading } = useSocialConfigs();
    const { setAlert } = useAlert();

    const [formData, setFormData] = useState({
        platform: '',
        username: '',
        url: '',
        active: true,
        order: 0
    });
    const [isLoading, setIsLoading] = useState(false); // Estado de loading local para o formulário

    const fetchConfigData = useCallback(async () => {
        if (isEditing) {
            setIsLoading(true);
            try {
                const response = await getSocialConfig(id);
                // Ajuste aqui: a resposta do backend pode ter a config em 'data' ou diretamente
                // Assuming getSocialConfig already returns { success: true, data: { ...config } }
                setFormData(response.data); // ✅ Ajustado para response.data, não response.data.data
            } catch (error) {
                // O useSocialConfigs já lida com o setAlert para getSocialConfig
            } finally {
                setIsLoading(false);
            }
        } else {
            // Define a plataforma inicial como a primeira opção disponível se houver
            // Isso deve acontecer APENAS se não houver um ID e se platformsOptions já carregou
            if (platformsOptions.length > 0 && !formData.platform) {
                setFormData(prev => ({ ...prev, platform: platformsOptions[0] }));
            }
        }
    }, [id, isEditing, getSocialConfig, platformsOptions, formData.platform]); // Adicionado formData.platform como dependência para evitar loop infinito na criação

    useEffect(() => {
        // Quando platformsOptions carrega ou é atualizado, se estivermos criando,
        // garantimos que a plataforma padrão seja definida.
        if (!isEditing && platformsOptions.length > 0 && !formData.platform) {
            setFormData(prev => ({ ...prev, platform: platformsOptions[0] }));
        }
    }, [platformsOptions, isEditing, formData.platform]); // Dependências ajustadas

    useEffect(() => {
        fetchConfigData();
    }, [fetchConfigData]); // Dependência ajustada

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            if (isEditing) {
                await updateSocialConfig(id, formData);
            } else {
                await createSocialConfig(formData);
            }
            navigate('/site-institucional/redes-sociais');
        } catch (error) {
            // O useSocialConfigs já lida com o setAlert para create/updateSocialConfig
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const totalLoading = hookLoading || isLoading;

    return (
        <div className="page-management-container">
            <form onSubmit={handleSubmit}>
                <PageHeader
                    title={isEditing ? 'Editar Rede Social' : 'Nova Rede Social'}
                    icon={<Share2 className="icon-title" />}
                    navBack={{ to: '/site-institucional/redes-sociais', text: 'Voltar' }}
                />

                <div className="content-panel mt-6">
                    <div className="form-grid-container grid-cols-1 md:grid-cols-2">
                        <div className="form-group">
                            <label>Plataforma*</label>
                            <select
                                name="platform"
                                value={formData.platform}
                                onChange={handleChange}
                                className="form-input"
                                required
                                disabled={isEditing || platformsOptions.length === 0} // ✅ Desabilita se não houver opções
                            >
                                <option value="" disabled>Selecione uma plataforma</option>
                                {platformsOptions.map(platform => (
                                    <option key={platform} value={platform}>{platform.charAt(0).toUpperCase() + platform.slice(1)}</option>
                                ))}
                            </select>
                            {isEditing && <p className="form-hint">Não é possível alterar a plataforma de uma rede social existente.</p>}
                        </div>

                        <div className="form-group">
                            <label>Nome de usuário/ID*</label>
                            <input
                                type="text"
                                name="username"
                                value={formData.username}
                                onChange={handleChange}
                                className="form-input"
                                required
                            />
                            <p className="form-hint">Ex: @suaempresa (Instagram/Twitter), SeuID (TikTok)</p>
                        </div>

                        <div className="form-group md:col-span-2">
                            <label>URL Completa da Rede Social*</label>
                            <input
                                type="url"
                                name="url"
                                value={formData.url}
                                onChange={handleChange}
                                className="form-input"
                                required
                            />
                            <p className="form-hint">Certifique-se de que a URL comece com "http://" ou "https://".</p>
                        </div>

                        <div className="form-group">
                            <label>Ordem de Exibição</label>
                            <input
                                type="number"
                                name="order"
                                value={formData.order}
                                onChange={handleChange}
                                className="form-input"
                            />
                            <p className="form-hint">Defina a ordem em que esta rede social aparecerá.</p>
                        </div>

                        <div className="form-group">
                            <label className="flex items-center mt-6">
                                <input
                                    type="checkbox"
                                    name="active"
                                    checked={formData.active}
                                    onChange={handleChange}
                                    className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                Ativo (Visível no site)
                            </label>
                        </div>
                    </div>
                </div>

                <FabSaveButton loading={totalLoading} label="Salvar" />
            </form>
        </div>
    );
};

export default RedesSociaisFormPage;